import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  MAX_JOB_FILE_BATCH,
  MAX_JOB_FILE_BYTES,
  JOB_FILE_LOCKED_CODE,
  jobFileInUseResponseSchema,
  jobFileLockedResponseSchema,
  type JobFileExpenseUsage,
  type JobFileInUseReport,
  type JobFileEntry,
  type JobFileListQuery,
  type JobFileOriginalUploadResponse,
  type UpdateJobFileInput,
} from '@jobos/shared';

import { APIError, apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import { downscaleForUpload } from '@/lib/image-downscale';
import { UPLOAD_CONCURRENCY, pool } from '@/lib/pool';
import type { MoveFilesResult } from '@/pages/jobs/components/files/jobFileMoveResult';

// The job file library's data layer. Reads and metadata mutations are ordinary
// React Query; the upload queue below is not, because a 50-photo batch from a
// phone has to stay responsive and bounded rather than fan out.

export const jobFileKeys = {
  all: ['jobFiles'] as const,
  /** Every list for a job, whatever its filters — the invalidation target. */
  lists: (jobId: string) => [...jobFileKeys.all, 'list', jobId] as const,
  list: (jobId: string, filters: JobFileListQuery) =>
    [...jobFileKeys.lists(jobId), filters] as const,
  url: (jobId: string, fileId: string, variant: JobFileUrlVariant) =>
    [...jobFileKeys.all, 'url', jobId, fileId, variant] as const,
};

/**
 * Both lists a change to what a folder holds can go stale in.
 *
 * The folder list carries each folder's `fileCount`, so adding, deleting or
 * moving a file dates it exactly as much as it dates the file list. Invalidating
 * only the file list leaves the folder tiles reading whatever they held when the
 * list was last fetched — most visible walking back up out of a subfolder you
 * just uploaded into, where the stale tile is the thing you're looking at.
 *
 * The folder key is spelled out rather than imported: useJobFolders imports
 * `jobFileKeys` from this module, so reaching for `jobFolderKeys` here would
 * close an import cycle.
 */
function invalidateFileAndFolderLists(queryClient: QueryClient, jobId: string): void {
  queryClient.invalidateQueries({ queryKey: jobFileKeys.lists(jobId) });
  queryClient.invalidateQueries({ queryKey: ['jobFolders'] });
}

export function useJobFiles(jobId: string | undefined, filters: JobFileListQuery = {}) {
  return useQuery({
    queryKey: jobFileKeys.list(jobId!, filters),
    queryFn: async () => {
      const res = await apiClient.get<{ files: JobFileEntry[] }>(`/jobs/${jobId}/files`, {
        params: {
          // Absent lists the whole job; null lists the job root. `null` has to
          // survive as a literal, since it means something different to absent.
          folderId: filters.folderId === null ? 'null' : filters.folderId,
          q: filters.q,
        },
      });
      return res.files;
    },
    enabled: !!jobId,
  });
}

/**
 * Which stored rendition to address. All three are inline URLs — this hook only
 * ever hands back something a component can render.
 *
 * Downloading is `downloadJobFile`'s job: it hits `/download`, which signs the
 * same object with an attachment disposition and the real filename.
 *
 * `thumb` and `original` fall back to the display rendition server-side when the
 * rendition asked for was never stored.
 */
export type JobFileUrlVariant = 'display' | 'thumb' | 'original';

// The backend presigns for 15 minutes. Going stale at 10 keeps a cached URL from
// being handed out with only seconds left on it.
const URL_STALE_MS = 10 * 60 * 1000;
const URL_GC_MS = 12 * 60 * 1000;

async function requestJobFileUrl(
  jobId: string,
  fileId: string,
  variant: JobFileUrlVariant,
): Promise<string> {
  const res = await apiClient.get<{ url: string }>(
    `/jobs/${jobId}/files/${fileId}/url`,
    variant === 'display' ? {} : { params: { variant } },
  );
  return res.url;
}

/**
 * A file's URL outside of render, through the same cache entry the hook fills.
 *
 * The inspection editor picks a photo and needs its URL then and there, not on
 * the next render of a component it doesn't have. Going through `fetchQuery`
 * rather than calling the API directly means a photo the picker already
 * thumbnailed is free, and the 15-minute expiry is still handled in one place.
 */
export function fetchJobFileUrl(
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
  fileId: string,
  variant: JobFileUrlVariant = 'display',
): Promise<string> {
  return queryClient.fetchQuery({
    queryKey: jobFileKeys.url(jobId, fileId, variant),
    queryFn: () => requestJobFileUrl(jobId, fileId, variant),
    staleTime: URL_STALE_MS,
    gcTime: URL_GC_MS,
  });
}

/**
 * @param options.enabled Gate on top of the id checks. The grid holds a card's
 * URL request until the card scrolls into view — one presign per file is the
 * cost that has to stay off the initial render.
 */
export function useJobFileUrl(
  jobId: string | undefined,
  fileId: string | undefined,
  variant: JobFileUrlVariant = 'display',
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: jobFileKeys.url(jobId!, fileId!, variant),
    queryFn: () => requestJobFileUrl(jobId!, fileId!, variant),
    enabled: !!jobId && !!fileId && (options.enabled ?? true),
    staleTime: URL_STALE_MS,
    gcTime: URL_GC_MS,
  });
}

export function useUpdateJobFile(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateJobFileInput }) => {
      const res = await apiClient.patch<{ file: JobFileEntry }>(`/jobs/${jobId}/files/${id}`, input);
      return res.file;
    },
    onSuccess: (updated) => {
      // Patched into the cache first so the row re-renders with the new metadata
      // in the same frame. Invalidating alone would be correct but arrives a
      // round trip later, and the edited row sits stale until it does.
      queryClient.setQueriesData<JobFileEntry[]>(
        { queryKey: jobFileKeys.lists(jobId) },
        (files) => {
          if (!files?.some((file) => file.id === updated.id)) return files;
          return files.map((file) => (file.id === updated.id ? updated : file));
        },
      );
      // Still needed: a folder change can move the file out of a filtered list,
      // and only the server knows what that list holds now.
      queryClient.invalidateQueries({ queryKey: jobFileKeys.lists(jobId) });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update file.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * The inspection reports a failed delete named, or null if it named none.
 *
 * Null covers every shape that isn't a reports-bearing 409 — a plain error, a
 * different status, a 409 whose body doesn't parse. The caller shows an ordinary
 * failure for those, because a blocked dialog listing nothing is worse than the
 * message it replaced.
 */
export function jobFileInUseReports(error: unknown): JobFileInUseReport[] | null {
  if (!(error instanceof APIError) || error.status !== 409) return null;
  const parsed = jobFileInUseResponseSchema.safeParse({ error: error.message, ...error.data });
  return parsed.success ? parsed.data.reports : null;
}

/**
 * Whether a failed move was refused because an expense references the file.
 *
 * The dialog says *why* rather than "failed", so the user knows the fix is
 * deleting the expense rather than picking a different folder.
 */
export function jobFileLockedError(error: unknown): boolean {
  return error instanceof APIError && error.status === 409 && error.code === JOB_FILE_LOCKED_CODE;
}

/**
 * The expenses a failed delete named, or null if it named none.
 *
 * Same contract as {@link jobFileInUseReports}: null means "show this as an
 * ordinary failure", which is what a refused *move* is — it carries the same
 * code but no list, because the move dialog has its own way of saying why.
 */
export function jobFileLockedExpenses(error: unknown): JobFileExpenseUsage[] | null {
  if (!(error instanceof APIError) || error.status !== 409) return null;
  const parsed = jobFileLockedResponseSchema.safeParse({ error: error.message, ...error.data });
  return parsed.success ? parsed.data.expenses : null;
}

/**
 * Move a batch of files into a folder.
 *
 * One request per file, because that is the endpoint the server offers and a
 * selection here is a handful of photos. What a bulk endpoint would buy — one
 * round trip — is not worth a new route, service and schema, and it would force
 * an all-or-nothing answer where the useful answer is per-file: one receipt
 * locked to an expense should not block a twenty-photo tidy-up.
 *
 * Resolves rather than throws, because a batch has no single outcome to throw
 * about. The caller decides how to say what happened.
 */
export function useMoveJobFiles(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ids,
      folderId,
    }: {
      ids: string[];
      folderId: string | null;
    }): Promise<MoveFilesResult> => {
      const outcomes = await Promise.allSettled(
        ids.map((id) =>
          apiClient.patch<{ file: JobFileEntry }>(`/jobs/${jobId}/files/${id}/folder`, {
            folderId,
          }),
        ),
      );

      const result: MoveFilesResult = { moved: [], locked: [], failed: [] };
      outcomes.forEach((outcome, index) => {
        const id = ids[index]!;
        if (outcome.status === 'fulfilled') result.moved.push(id);
        else if (jobFileLockedError(outcome.reason)) result.locked.push(id);
        else result.failed.push(id);
      });
      return result;
    },
    onSuccess: (result) => {
      // One invalidation for the batch, not one per file: every file that moved
      // left one list and joined another, and only the server knows what each
      // holds now.
      if (result.moved.length > 0) invalidateFileAndFolderLists(queryClient, jobId);
    },
  });
}

export function useDeleteJobFile(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/jobs/${jobId}/files/${id}`);
    },
    // Optimistic because the confirmation already happened: waiting on the round
    // trip would leave the row sitting there after the user said delete. The
    // snapshot below is what makes that safe to undo.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: jobFileKeys.lists(jobId) });
      const snapshot = queryClient.getQueriesData<JobFileEntry[]>({
        queryKey: jobFileKeys.lists(jobId),
      });
      queryClient.setQueriesData<JobFileEntry[]>(
        { queryKey: jobFileKeys.lists(jobId) },
        (files) => files?.filter((file) => file.id !== id),
      );
      return { snapshot };
    },
    onError: (error: Error, _id, context) => {
      // Every failure puts the row back, including the blocked one — the file is
      // still there, and the dialog is about to say which reports are holding it.
      for (const [key, files] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, files);
      }
      // A 409 carries the inspection reports still using the photo, and the
      // caller names them in a dialog. Toasting here would replace that with a
      // line of text that can't say which reports.
      if (error instanceof APIError && error.status === 409) return;
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete file.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      invalidateFileAndFolderLists(queryClient, jobId);
    },
  });
}

// ---------------------------------------------------------------------------
// Upload queue
//
// Every file goes through two phases. Phase 1 uploads the downscaled display
// rendition through the API and creates the row — the file is visible and usable
// the moment that lands. Phase 2 PUTs the untouched original straight to R2 and
// records the key, and is queued behind *every* phase 1, so a 50-photo batch
// becomes browsable long before a single original has finished.
//
// Phase 2 is best-effort by design: an abandoned or failed original leaves the
// row with originalKey null, which every read path already treats as "serve the
// display rendition". That is why a phase 2 failure never marks the file failed.
//
// Queue state is in memory. Navigating away discards it; a resumable queue is
// deliberately out of scope.
// ---------------------------------------------------------------------------

export type JobFileUploadStatus = 'pending' | 'uploading' | 'done' | 'failed';
export type JobFileOriginalStatus = 'none' | 'pending' | 'uploading' | 'done' | 'failed';

export interface JobFileUploadItem {
  /** Queue-local id. The server row id lands in `fileId` once phase 1 succeeds. */
  id: string;
  file: File;
  /** Phase 1 only — `done` means the file is usable, not that the original landed. */
  status: JobFileUploadStatus;
  /** Phase 2. `none` when there is no separate original to upload. */
  originalStatus: JobFileOriginalStatus;
  error?: string;
  fileId?: string;
  /** The folder the batch was dropped into. Undefined lands it at the job root. */
  folderId?: string;
}

/** Re-exported under its historical name; the limiter itself lives in lib/pool. */
export const JOB_FILE_UPLOAD_CONCURRENCY = UPLOAD_CONCURRENCY;

/** Per-item working data that must not trigger a render: the original's handle. */
interface UploadWork {
  original: File | null;
  fileId: string;
}

/**
 * Multipart upload of the display rendition.
 *
 * Bypasses apiClient, which forces application/json, and hits the backend
 * directly — the same reason useUploadReceipt does.
 */
async function uploadDisplayRendition(
  jobId: string,
  file: File,
  folderId?: string,
): Promise<JobFileEntry> {
  const formData = new FormData();
  formData.append('file', file);
  // Omitted for the job root, which is where a file goes when the user is not
  // inside a folder. Nothing is inferred from the content type.
  if (folderId) formData.append('folderId', folderId);

  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const response = await fetch(`${apiBaseUrl}/jobs/${jobId}/files`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || 'Upload failed');
  }

  const data = (await response.json()) as { file: JobFileEntry };
  return data.file;
}

/**
 * Phase 2 for one file: presign, PUT the untouched bytes, record the key.
 *
 * Shared with the single-photo upload at the foot of this file, which runs it
 * detached — every caller treats a rejection as "this row keeps originalKey
 * null", never as a failed upload.
 */
async function uploadOriginalRendition(jobId: string, fileId: string, original: File): Promise<void> {
  const presigned = await apiClient.post<JobFileOriginalUploadResponse>(
    `/jobs/${jobId}/files/${fileId}/original-url`,
    { contentType: original.type, sizeBytes: original.size },
  );

  // Straight to R2: a different origin, where the signature is the whole
  // authorization, so no session cookie goes with it. Content-Type has to
  // match what the signature was cut against or the PUT is rejected.
  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: original,
    headers: { 'Content-Type': presigned.contentType },
  });
  if (!put.ok) throw new Error(`Original upload failed (${put.status})`);

  await apiClient.patch(`/jobs/${jobId}/files/${fileId}/original`, {
    originalKey: presigned.key,
    originalSizeBytes: original.size,
  });
}

export function useJobFileUpload(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [items, setItems] = useState<JobFileUploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // The async runners can't read `items` — they'd close over a stale render. The
  // ref is the authority; the state is its mirror for the UI.
  const itemsRef = useRef<JobFileUploadItem[]>([]);
  const workRef = useRef(new Map<string, UploadWork>());
  const nextIdRef = useRef(0);
  // Batches are chained rather than run side by side, so the concurrency ceiling
  // holds across a second enqueue instead of only within one batch.
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const activeBatchesRef = useRef(0);

  const writeItems = useCallback((update: (prev: JobFileUploadItem[]) => JobFileUploadItem[]) => {
    itemsRef.current = update(itemsRef.current);
    setItems(itemsRef.current);
  }, []);

  /** No-ops when the id is gone, so a clear() mid-flight can't resurrect an item. */
  const patchItem = useCallback(
    (id: string, patch: Partial<JobFileUploadItem>) => {
      writeItems((prev) => {
        let found = false;
        const next = prev.map((item) => {
          if (item.id !== id) return item;
          found = true;
          return { ...item, ...patch };
        });
        return found ? next : prev;
      });
    },
    [writeItems],
  );

  const runPhase1 = useCallback(
    async (item: JobFileUploadItem) => {
      patchItem(item.id, { status: 'uploading', error: undefined });
      try {
        // Downscaled here, immediately before its own upload, rather than for the
        // whole batch up front: one decoded bitmap in flight keeps mobile memory
        // flat across 50 photos.
        const { display, original } = await downscaleForUpload(item.file);
        const created = await uploadDisplayRendition(jobId, display, item.folderId);

        workRef.current.set(item.id, { original, fileId: created.id });
        patchItem(item.id, {
          status: 'done',
          fileId: created.id,
          originalStatus: original ? 'pending' : 'none',
          error: undefined,
        });
      } catch (err) {
        // Only this file. The pool keeps pulling.
        workRef.current.delete(item.id);
        patchItem(item.id, {
          status: 'failed',
          originalStatus: 'none',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      }
    },
    [jobId, patchItem],
  );

  const runPhase2 = useCallback(
    async (item: JobFileUploadItem) => {
      const work = workRef.current.get(item.id);
      if (!work?.original) return;
      const { original, fileId } = work;

      // An original over the cap can never be presigned — the route refuses it
      // — so attempting it would leave a failed row offering a Retry that
      // always fails. The file is already usable at its display rendition.
      if (original.size > MAX_JOB_FILE_BYTES) {
        workRef.current.set(item.id, { original: null, fileId });
        patchItem(item.id, { originalStatus: 'none' });
        return;
      }

      patchItem(item.id, { originalStatus: 'uploading' });
      try {
        await uploadOriginalRendition(jobId, fileId, original);

        // Release the handle; nothing retries a landed original.
        workRef.current.set(item.id, { original: null, fileId });
        patchItem(item.id, { originalStatus: 'done' });
      } catch {
        // The file is already usable — it just keeps originalKey null. The handle
        // stays put so retryAllFailed can have another go.
        patchItem(item.id, { originalStatus: 'failed' });
      }
    },
    [jobId, patchItem],
  );

  const runBatch = useCallback(
    async (batch: JobFileUploadItem[]) => {
      // Snapshots taken when the batch was scheduled: 'done' means phase 1 already
      // succeeded and this is an original-only retry.
      const phase1 = batch.filter((item) => item.status !== 'done');
      await pool(phase1, JOB_FILE_UPLOAD_CONCURRENCY, runPhase1);

      // Once, here. The rows exist and the batch is browsable; phase 2 only sets
      // originalKey, which every read path already falls back from while null.
      invalidateFileAndFolderLists(queryClient, jobId);

      const phase2 = batch.filter((item) => workRef.current.get(item.id)?.original);
      await pool(phase2, JOB_FILE_UPLOAD_CONCURRENCY, runPhase2);
    },
    [jobId, queryClient, runPhase1, runPhase2],
  );

  const schedule = useCallback(
    (batch: JobFileUploadItem[]) => {
      if (batch.length === 0) return;
      activeBatchesRef.current += 1;
      setIsUploading(true);
      chainRef.current = chainRef.current
        .then(() => runBatch(batch))
        .catch(() => {
          // runBatch already recorded per-item failure; nothing escapes it.
        })
        .then(() => {
          activeBatchesRef.current -= 1;
          if (activeBatchesRef.current === 0) setIsUploading(false);
        });
    },
    [runBatch],
  );

  const enqueue = useCallback(
    (files: File[] | FileList, options: { folderId?: string | null } = {}) => {
      const selected = Array.from(files);
      if (selected.length === 0) return;

      if (selected.length > MAX_JOB_FILE_BATCH) {
        toast({
          title: 'Too many files',
          description: `Upload up to ${MAX_JOB_FILE_BATCH} files at a time — you picked ${selected.length}.`,
          variant: 'destructive',
        });
        return;
      }

      const batch: JobFileUploadItem[] = selected.map((file) => ({
        id: `jf-${nextIdRef.current++}`,
        file,
        status: 'pending',
        originalStatus: 'none',
        ...(options.folderId ? { folderId: options.folderId } : {}),
      }));

      writeItems((prev) => [...prev, ...batch]);
      schedule(batch);
    },
    [schedule, toast, writeItems],
  );

  const retry = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((entry) => entry.id === id);
      if (!item) return;
      // Either phase can be the failed one. A failed phase 1 re-runs both; a file
      // that uploaded but whose original didn't re-runs phase 2 alone, because its
      // snapshot status is already 'done'.
      if (item.status !== 'failed' && item.originalStatus !== 'failed') return;
      schedule([item]);
    },
    [schedule],
  );

  const retryAllFailed = useCallback(() => {
    const failed = itemsRef.current.filter(
      (item) => item.status === 'failed' || item.originalStatus === 'failed',
    );
    schedule(failed);
  }, [schedule]);

  /**
   * Drop the rows with nothing left to say: phase 1 landed and the original
   * either landed or never existed. A failure stays — it is still retryable —
   * and so does an original still in flight, which the background line counts.
   */
  const clearSettled = useCallback(() => {
    writeItems((prev) => {
      const next = prev.filter(
        (item) =>
          !(
            item.status === 'done' &&
            (item.originalStatus === 'none' || item.originalStatus === 'done')
          ),
      );
      // The same array back when nothing settled, so an effect can call this freely.
      if (next.length === prev.length) return prev;
      for (const item of prev) {
        if (!next.includes(item)) workRef.current.delete(item.id);
      }
      return next;
    });
  }, [writeItems]);

  const clear = useCallback(() => {
    writeItems(() => []);
    workRef.current.clear();
  }, [writeItems]);

  return { items, enqueue, retry, retryAllFailed, clearSettled, clear, isUploading };
}

/**
 * One photo into the job's library, handing the created row back to the caller.
 *
 * The inspection editor uploads through here rather than through the queue
 * above: it has a single file and one thing to do with the result — fill the
 * slot it was dropped on — so it needs the row, not a progress list. Going
 * through the library at all is the point, though. A photo shot for a report is
 * a photo of the job, and it used to land in a prefix the Files tab never saw.
 */
export function useUploadJobPhoto(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File): Promise<JobFileEntry> => {
      const { display, original } = await downscaleForUpload(file);
      // No folder: a photo shot for a report belongs to the job, not to one of
      // the user's folders.
      const created = await uploadDisplayRendition(jobId, display);

      // Detached, exactly as phase 2 is for a batch: the caller fills its slot
      // from the display rendition, and a full-size original over a phone
      // connection must not hold that up. A failure leaves originalKey null,
      // which every read path already falls back from.
      if (original) void uploadOriginalRendition(jobId, created.id, original).catch(() => {});

      return created;
    },
    // Invalidated before the original has landed, deliberately: the row is
    // already usable, and the Files tab refetches when it next mounts, which is
    // the earliest anyone could look for the original anyway.
    onSuccess: () => {
      invalidateFileAndFolderLists(queryClient, jobId);
    },
    onError: (error: Error) => {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload photo.',
        variant: 'destructive',
      });
    },
  });
}
